import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { ButtonLoaderComponent } from '../../../../shared/components/button-loader/button-loader.component';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { CustomEditorComponent } from '../../../../shared/components/custom-editor/custom-editor.component';
import { UploadImageComponent } from '../../../../shared/components/upload-image/upload-image.component';
import { MediaSignedUrlModel } from '../../../../shared/interface/interface';
import {
  AuthorModel,
  AuthorPayloadModel,
} from '../../../../shared/models/author.model';
import { AuthorService } from '../../../../shared/services/author.service';
import { SharedService } from '../../../../shared/services/shared.service';
import { SharedModule } from '../../../../shared/shared.module';

@Component({
  selector: 'app-add-edit-authors',
  standalone: true,
  imports: [
    SharedModule,
    ButtonComponent,
    ButtonLoaderComponent,
    UploadImageComponent,
    CustomEditorComponent,
  ],
  templateUrl: './add-edit-authors.component.html',
  styleUrl: './add-edit-authors.component.scss',
})
export class AddEditAuthorsComponent implements OnInit {
  authorForm: FormGroup = this.fb.group({
    name: ['', [Validators.required]],
    bio: ['', [Validators.required]],
    profileImage: ['', [Validators.required]],
    description: ['', [Validators.required]],
  });

  authorId: string | null = null;
  subscription: Subscription = new Subscription();
  isLoading: boolean = false;
  profileImagePreviewUrl = '';
  editor!: any;
  initialLoading = false;
  imageUploading = false;

  constructor(
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private sharedService: SharedService,
    private router: Router,
    private authorService: AuthorService,
    private toastr: ToastrService
  ) {}
  ngOnInit(): void {
    this.init();
  }

  init() {
    const sub = this.activatedRoute.paramMap.subscribe((data: any) => {
      this.authorId = data.get('id');
      if (this.authorId) {
        this.setInititalAuthorValue(this.authorId);
      }
    });
    this.subscription.add(sub);
  }
  onSubmit() {
    if (
      !this.authorForm.valid ||
      this.isLoading ||
      this.initialLoading ||
      this.imageUploading
    ) {
      this.authorForm.markAllAsTouched();
      return;
    }
    this.isLoading = true;

    const payload: AuthorPayloadModel = this.authorForm
      .value as AuthorPayloadModel;

    if (this.authorId) {
      this.authorService.updateAuthor(this.authorId, payload).subscribe({
        next: (data: any) => {
          this.toastr.success('Author updated successfully!');
          this.router.navigate(['/authors']);
          this.isLoading = false;
        },
        error: (err) => {
          this.toastr.error(
            err?.error?.message || err?.message || 'Something went wrong!'
          );
          this.isLoading = false;
        },
      });
    } else {
      this.authorService.addAuthor(payload).subscribe({
        next: (data: any) => {
          this.toastr.success('Author added successfully!');
          this.router.navigate(['/authors']);
          this.isLoading = false;
        },
        error: (err) => {
          this.toastr.error(
            err?.error?.message || err?.message || 'Something went wrong!'
          );
          this.isLoading = false;
        },
      });
    }
  }
  setInititalAuthorValue(id: string) {
    this.initialLoading = true;
    this.authorService.fetchAuthorById(id).subscribe({
      next: (data: { result: AuthorModel }) => {
        this.initialLoading = false;
        this.authorForm.setValue({
          name: data.result.name,
          bio: data.result.bio,
          profileImage: data.result.profileImage,
          description: data.result.description,
        });
        this.profileImagePreviewUrl = data.result.profileImagePreviewUrl;
      },
      error: (err: any) => {
        this.initialLoading = false;
        this.toastr.error(
          err?.error?.message || err?.message || 'Something went wrong!'
        );
      },
    });
  }

  removeImage() {
    this.authorForm.get('profileImage')?.setValue('');
    this.profileImagePreviewUrl = '';
  }

  onUploadStart() {
    this.imageUploading = true;
  }

  onUploadEnd(data: MediaSignedUrlModel | null) {
    this.imageUploading = false;
    if (data) {
      this.authorForm.get('profileImage')?.setValue(data.key);
      this.profileImagePreviewUrl = data.contentUrl;
    }
  }

  cancel() {
    this.router.navigate(['/authors']);
  }
}
